// Library route state and side effects used by the library layout shell.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import {
  useAuth,
  useAuthDialogStep,
  useAuthPostLogin,
  useRouteQueryState,
  useRouteTwitchError,
} from '../../../hooks';
import { analyticsService } from '../../../services/analyticsService';
import { useLibraryDialog } from './useLibraryDialog';
import { useLibraryModCensorship } from './useLibraryModCensorship';
import { useLibraryModPlayerAccess } from './useLibraryModPlayerAccess';
import { useLibraryPoppyChat } from './useLibraryPoppyChat';
import {
  LIBRARY_QUERY_CONFIG,
  type LibraryRouteEnumState,
  type LibraryRouteFacetState,
  type LibraryPanel,
} from '../libraryRouteTypes';

export const useLibraryLayoutState = () => {
  const { user, isAuthInitialized, setUser } = useAuth();
  const [pendingModScopeDenied, setPendingModScopeDenied] = useState<
    'blocked-users' | 'blocked-twitch-terms' | null
  >(null);
  const [connectionsTwitchError, setConnectionsTwitchError] = useState<
    string | null
  >(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { values, updateEnums } = useRouteQueryState<
    LibraryRouteFacetState,
    LibraryRouteEnumState
  >({
    pathname: location.pathname,
    searchParams,
    navigate,
    ...LIBRARY_QUERY_CONFIG,
  });
  const { dialog, openDialog, setDialog } = useLibraryDialog();
  const panel = values.section;
  const isBlockedUsersScopeDeniedReturn =
    dialog === 'blocked-users' && searchParams.get('error') === 'access_denied';
  const isBlockedTermsScopeDeniedReturn =
    dialog === 'blocked-twitch-terms' &&
    searchParams.get('error') === 'access_denied';
  const requireAuth = useCallback(() => {
    openDialog('login');
  }, [openDialog]);
  const playerAccess = useLibraryModPlayerAccess({
    user,
    dialog,
    openDialog,
  });
  const censorship = useLibraryModCensorship({
    user,
    dialog,
    openDialog,
    requireAuth,
  });
  const poppyChat = useLibraryPoppyChat({
    user,
    isPanelOpen: panel === 'poppy-chat',
    requireAuth,
    openDialog,
  });

  const setPanel = useCallback(
    (nextPanel: LibraryPanel | null) => {
      updateEnums({ section: nextPanel }, { replace: true });
    },
    [updateEnums]
  );

  const closeLoginDialog = useCallback(() => {
    setDialog(null);
  }, [setDialog]);

  const { step: authStep, setStep: setAuthStep } = useAuthDialogStep({
    initialStep: values.loginStep,
    isDialogOpen: dialog === 'login',
  });

  const showWelcome = useCallback(() => {
    setAuthStep('welcome');
    setDialog('login');
  }, [setAuthStep, setDialog]);

  useAuthPostLogin({
    user,
    isAuthInitialized,
    analyticsSurface: 'library',
    showWelcome,
    closeDialog: closeLoginDialog,
    setUser,
  });

  const { error: twitchRouteError } = useRouteTwitchError({
    toastOnError: dialog !== 'connections',
  });

  useEffect(() => {
    if (dialog !== 'connections') {
      setConnectionsTwitchError(null);
      return;
    }

    if (twitchRouteError) {
      setConnectionsTwitchError(twitchRouteError);
    }
  }, [dialog, twitchRouteError]);

  useEffect(() => {
    if (isBlockedUsersScopeDeniedReturn) {
      setPendingModScopeDenied('blocked-users');
      setDialog(null);
      return;
    }

    if (isBlockedTermsScopeDeniedReturn) {
      setPendingModScopeDenied('blocked-twitch-terms');
      setDialog(null);
    }
  }, [
    isBlockedTermsScopeDeniedReturn,
    isBlockedUsersScopeDeniedReturn,
    setDialog,
  ]);

  useEffect(() => {
    if (!pendingModScopeDenied || !isAuthInitialized) {
      return;
    }

    const deniedTarget = pendingModScopeDenied;
    setPendingModScopeDenied(null);
    if (!user) {
      return;
    }

    if (deniedTarget === 'blocked-users') {
      playerAccess.handleModScopeDenied();
      return;
    }

    censorship.handleModScopeDenied();
  }, [
    censorship,
    isAuthInitialized,
    pendingModScopeDenied,
    playerAccess,
    user,
  ]);
  useEffect(() => {
    if (searchParams.get('purchased') !== 'true') {
      return;
    }

    analyticsService.trackPurchaseCompleted({
      surface: 'library',
      flow_type: 'post_checkout_return',
      purchased: true,
      checkout_result: 'succeeded',
    });

    toast.success('Purchase successful! Your Loons have been added.');

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('purchased');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  /** Heading for the mobile title bar; desktop reads the sidebar instead. */
  const mobileTitle = useMemo(() => {
    if (location.pathname === '/library/poppy') {
      return 'Host Style';
    }

    if (location.pathname === '/world') {
      return 'World';
    }

    if (location.pathname === '/library/achievements') {
      return 'Achievements';
    }

    return 'Game Library';
  }, [location.pathname]);

  return {
    user,
    pathname: location.pathname,
    panel,
    dialog,
    playerAccess,
    censorship,
    poppyChat,
    connectionsTwitchError,
    setPanel,
    setDialog,
    openDialog,
    authStep,
    setAuthStep,
    mobileTitle,
  };
};
